import { api } from "@bindery/convex-reader/api";
import { useQuery } from "convex/react";
import { useEffect, useState } from "react";
import { readerIdOf, sessionStore, useSession } from "@/convex/session";
import type { BookmarkRow } from "@/storage/db";
import { bookmarks } from "@/sync";

/**
 * This device's mirror first, so the shelf paints offline and before the
 * network answers, then `library.get` merged over it under the reader the
 * server confirmed.
 */
export function useBookmarks(): BookmarkRow[] {
  const readerId = readerIdOf(useSession());
  const server = useQuery(
    api.library.get,
    readerId === null ? "skip" : { expectedReaderId: readerId },
  );
  const [rows, setRows] = useState<BookmarkRow[]>([]);

  useEffect(() => {
    if (readerId === null) return;
    let live = true;
    void bookmarks.mirror().then((mirrored) => {
      if (live) setRows(mirrored);
    });
    return () => {
      live = false;
    };
  }, [readerId]);

  useEffect(() => {
    if (!server || readerId === null) return;
    if ("readerMismatch" in server) {
      void sessionStore.refresh();
      return;
    }
    let live = true;
    void bookmarks.mergeServer(readerId, server.bookmarks).then((merged) => {
      if (live) setRows(merged);
    });
    return () => {
      live = false;
    };
  }, [server, readerId]);

  // A switch of reader leaves the last reader's rows in state for a render.
  return rows.filter((row) => row.readerId === readerId);
}
